import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, PencilSimple, Trash, Eye, FileText } from "@phosphor-icons/react";
import { TemplateFormDialog } from "./TemplateFormDialog";
import { TemplateBody, ModeSwitch } from "./TemplatePicker";
import { fmtDateTime } from "./render";

export function LeadTemplates() {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(null);
  const [view, setView] = useState(null);
  const [mode, setMode] = useState("vars");
  const [toDelete, setToDelete] = useState(null);
  const load = () => api.get("/admin/leads/templates").then(({ data }) => setList(data.templates)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const del = async () => {
    try { await api.delete(`/admin/leads/templates/${toDelete.id}`); toast.success("Template supprimé"); setToDelete(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="surface rounded-lg p-5" data-testid="lead-templates">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div><div className="overline">Templates d'email</div><p className="text-xs text-muted mt-1">Proposés lors de la préparation de chaque campagne. Variables : {"{{prenom}} {{nom}} {{entreprise}}"}.</p></div>
        <button onClick={() => setForm({})} className="flex items-center gap-1.5 text-xs border border-accent/40 text-accent rounded-md px-3 py-1.5 hover:bg-accent/10" data-testid="lead-template-new"><Plus size={13} /> Nouveau template</button>
      </div>
      {list === null && <p className="text-sm text-muted animate-pulse">Chargement…</p>}
      {list?.length === 0 && (
        <div className="text-sm text-muted flex items-center gap-2 py-3" data-testid="lead-templates-empty"><FileText size={16} /> Aucun template. Créez-en un pour le réutiliser dans vos campagnes.</div>
      )}
      {list?.map((t) => (
        <div key={t.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border/50 py-2" data-testid={`lead-template-${t.id}`}>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-fg truncate">{t.name}</div>
            <div className="text-xs text-muted truncate">{t.subject} · v{t.version} · modifié le {fmtDateTime(t.updated_at)}{t.updated_by ? ` par ${t.updated_by}` : ""}</div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setView(t)} title="Aperçu" className="p-1.5 text-muted hover:text-accent" data-testid={`lead-template-view-${t.id}`}><Eye size={15} /></button>
            <button onClick={() => setForm({ template: t })} title="Modifier" className="p-1.5 text-muted hover:text-accent" data-testid={`lead-template-edit-${t.id}`}><PencilSimple size={15} /></button>
            <button onClick={() => setToDelete(t)} title="Supprimer" className="p-1.5 text-muted hover:text-destructive" data-testid={`lead-template-delete-${t.id}`}><Trash size={15} /></button>
          </div>
        </div>
      ))}
      <TemplateFormDialog open={!!form} onClose={() => setForm(null)} template={form?.template} existing={list || []} onSaved={load} />
      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent className="bg-surface border-border w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-template-view-dialog">
          <DialogHeader><DialogTitle>{view?.name}</DialogTitle></DialogHeader>
          <ModeSwitch mode={mode} setMode={setMode} testid="lead-template-view-mode" />
          {view && <TemplateBody t={view} mode={mode} />}
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-surface border-border" data-testid="lead-template-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le template « {toDelete?.name} » ?</AlertDialogTitle>
            <AlertDialogDescription>Suppression définitive. Les campagnes déjà préparées ou envoyées ne sont pas modifiées.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lead-template-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-destructive text-white hover:bg-destructive/90" data-testid="lead-template-delete-confirm">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
